import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Check, Code2, Copy, Globe2, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, BACKEND_URL } from "@/lib/api";
import { clearWebsiteSetupIntent, websiteFromSearch } from "@/lib/websiteSetup";

async function copyText(value) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(value);
  const area = document.createElement("textarea");
  area.value = value;
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  document.execCommand("copy");
  area.remove();
}

export function SiteTrackerSetup({ site, onSiteCreated }) {
  const location = useLocation();
  const requestedWebsite = websiteFromSearch(location.search);
  const [domain, setDomain] = useState(requestedWebsite || site.domain || "");
  useEffect(() => { if (requestedWebsite) clearWebsiteSetupIntent(); }, [requestedWebsite]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const snippet = site.site_id ? `<script async src="${BACKEND_URL}/api/tracker.js" data-site="${site.site_id}"></script>` : "Connect a site to generate your tracking snippet.";

  const createSite = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/sites", { domain });
      onSiteCreated(data);
      toast.success(`Tracking is ready for ${data.domain}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Enter a valid website domain");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await copyText(snippet);
      setCopied(true);
      toast.success("HTML tracking snippet copied");
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.info("Select the snippet and copy it manually");
    }
  };

  return <section className="site-setup" data-testid="site-tracker-setup">
    <div className="setup-heading"><span className="setup-icon"><Globe2 size={22}/></span><div><span>Tracked website</span><h2>Connect your site</h2><p>Enter your domain to create a unique tracking ID and installation snippet.</p></div></div>
    <div className="domain-builder">
      <Input value={domain} onChange={(event) => setDomain(event.target.value)} placeholder="yourwebsite.com" data-testid="tracking-domain-input"/>
      <Button onClick={createSite} disabled={loading || domain.trim().length < 3} data-testid="generate-tracking-snippet-button">{loading ? <LoaderCircle className="spin" size={17}/> : <Code2 size={17}/>} Generate snippet</Button>
    </div>
    <div className="site-identity" data-testid="active-tracking-site"><span>Active site</span><strong>{site.domain || "No site connected"}</strong><code>{site.site_id || "Waiting for setup"}</code></div>
    <div className="snippet-box"><code data-testid="tracking-snippet-code">{snippet}</code><Button onClick={copy} disabled={!site.site_id} variant="outline" data-testid="copy-tracking-snippet-button">{copied ? <Check size={17}/> : <Copy size={17}/>} {copied ? "Copied" : "Copy HTML"}</Button></div>
    <div className="install-steps" data-testid="tracking-install-steps"><span><b>1</b> Copy the HTML</span><span><b>2</b> Paste before &lt;/head&gt;</span><span><b>3</b> Open your website</span></div>
  </section>;
}
